import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { de } from '../lib/i18n/de';
import { useAuthOrSession } from '../lib/authOrSession';
import { getSupabaseClient, updatePassword, verifyRecoveryOtp } from '../lib/supabase';
import { firstIssueMessage, passwordPairSchema } from '../lib/authValidation';
import { Button } from '../components/ui/Button';

type ResetPhase =
  | { kind: 'exchanging' }
  | { kind: 'recovery' }
  | { kind: 'form' }
  | { kind: 'done' }
  | { kind: 'error'; message: string };

/**
 * Passwort-Reset-Route (/auth/reset).
 *
 * Dedizierte Route statt Type-Erkennung im AuthCallback: Im PKCE-Flow kommt
 * der Recovery-Link als `?code=` ohne verlässliches `type=recovery` an —
 * nur die Redirect-URL unterscheidet Recovery von Magic-Link. Deshalb zeigt
 * `resetPasswordForEmail` hierher und /auth/callback bleibt für Magic-Links
 * zuständig.
 *
 * Ablauf: `?code=` → exchangeCodeForSession (Pattern von AuthCallback) →
 * „Neues Passwort setzen“-Formular → updateUser({ password }) → /cases.
 * PKCE-Mismatch (Link in anderem Browser geöffnet) → Fallback-Formular mit
 * E-Mail + 6-stelligem Code (`verifyOtp type=recovery`).
 *
 * Im Dev-Mock wird der Exchange übersprungen (kein Supabase-Client) und das
 * Formular simuliert Erfolg.
 */
export function AuthReset() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { isMock } = useAuthOrSession();
  const [phase, setPhase] = useState<ResetPhase>(
    isMock ? { kind: 'form' } : { kind: 'exchanging' },
  );

  // Formular-State (neues Passwort)
  const [password, setPassword] = useState('');
  const [passwordRepeat, setPasswordRepeat] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Recovery-Fallback-State (PKCE-Mismatch)
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryOtp, setRecoveryOtp] = useState('');
  const [recoveryError, setRecoveryError] = useState<string | null>(null);
  const [recoverySubmitting, setRecoverySubmitting] = useState(false);

  // StrictMode-Guard: exchangeCodeForSession ist nicht idempotent (der Code
  // ist single-use) — Doppel-Mount in Dev darf den Exchange nicht wiederholen.
  const exchangeStartedRef = useRef(false);

  useEffect(() => {
    if (isMock) return;
    if (exchangeStartedRef.current) return;
    exchangeStartedRef.current = true;

    let cancelled = false;
    async function run() {
      const code = params.get('code');
      const errorDescription = params.get('error_description');

      if (errorDescription) {
        setPhase({ kind: 'error', message: errorDescription });
        return;
      }
      if (!code) {
        setPhase({ kind: 'error', message: de.authReset.errorBody });
        return;
      }
      try {
        const supa = getSupabaseClient();
        const { error } = await supa.auth.exchangeCodeForSession(code);
        if (cancelled) return;
        if (error) {
          // PKCE-Mismatch erkennen — gleiches Muster wie AuthCallback.
          const msg = error.message.toLowerCase();
          const isPkce = msg.includes('verifier') || msg.includes('pkce');
          setPhase(isPkce ? { kind: 'recovery' } : { kind: 'error', message: error.message });
          return;
        }
        setPhase({ kind: 'form' });
      } catch (err) {
        if (cancelled) return;
        setPhase({
          kind: 'error',
          message: err instanceof Error ? err.message : de.authReset.errorBody,
        });
      }
    }
    void run();
    return () => {
      cancelled = true;
    };
  }, [isMock, params]);

  const handleRecoverySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setRecoveryError(null);
    const emailTrimmed = recoveryEmail.trim().toLowerCase();
    if (!emailTrimmed.includes('@')) {
      setRecoveryError(de.authCallback.pkceEmailInvalid);
      return;
    }
    if (!/^\d{6}$/.test(recoveryOtp.trim())) {
      setRecoveryError(de.authCallback.pkceOtpInvalid);
      return;
    }
    setRecoverySubmitting(true);
    try {
      const res = await verifyRecoveryOtp(emailTrimmed, recoveryOtp.trim());
      if (!res.ok) {
        setRecoveryError(res.error ?? de.authCallback.pkceVerifyFailed);
        return;
      }
      setPhase({ kind: 'form' });
    } catch (err) {
      setRecoveryError(err instanceof Error ? err.message : de.authCallback.pkceVerifyFailed);
    } finally {
      setRecoverySubmitting(false);
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const parsed = passwordPairSchema.safeParse({ password, passwordRepeat });
    if (!parsed.success) {
      setFormError(firstIssueMessage(parsed));
      return;
    }
    setSubmitting(true);
    try {
      if (!isMock) {
        const res = await updatePassword(password);
        if (!res.ok) {
          setFormError(res.error ?? de.authReset.errorBody);
          return;
        }
      }
      setPhase({ kind: 'done' });
      window.setTimeout(() => {
        void navigate('/cases', { replace: true });
      }, 800);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : de.authReset.errorBody);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="mx-auto flex min-h-[60vh] max-w-prose flex-col justify-center px-5 py-16 md:px-8">
      <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
        {de.authReset.sectionLabel}
      </p>
      <h1 className="mt-3 font-serif text-4xl font-black leading-tight tracking-tight text-stone-900 md:text-5xl">
        {phase.kind === 'exchanging' && de.authReset.exchangingTitle}
        {phase.kind === 'recovery' && de.authReset.recoveryTitle}
        {phase.kind === 'form' && de.authReset.formTitle}
        {phase.kind === 'done' && de.authReset.doneTitle}
        {phase.kind === 'error' && de.authReset.errorTitle}
      </h1>

      {phase.kind === 'exchanging' && (
        <p className="mt-4 font-serif text-base italic text-stone-500">
          {de.authReset.exchangingBody}
        </p>
      )}

      {phase.kind === 'done' && (
        <p className="mt-4 font-serif text-base italic text-stone-500">{de.authReset.doneBody}</p>
      )}

      {phase.kind === 'error' && (
        <>
          <p className="mt-4 font-serif text-base text-red-700">{de.authReset.errorBody}</p>
          <details className="mt-4">
            <summary className="cursor-pointer font-sans text-xs font-medium uppercase tracking-widest text-stone-400 hover:text-stone-600">
              {de.authCallback.technicalDetails}
            </summary>
            <p className="mt-2 font-serif text-xs italic text-stone-500">{phase.message}</p>
          </details>
          <a
            href="/login"
            className="mt-6 inline-flex w-fit rounded-sm bg-stone-900 px-4 py-3 font-sans text-xs font-medium uppercase tracking-widest text-white transition-colors hover:bg-stone-800"
          >
            {de.authReset.backToLogin}
          </a>
        </>
      )}

      {phase.kind === 'recovery' && (
        <>
          <p className="mt-4 font-serif text-base text-stone-700">{de.authReset.recoveryBody}</p>
          <form
            onSubmit={(e) => void handleRecoverySubmit(e)}
            className="mt-8 rounded-sm border border-stone-200 bg-white p-6 md:p-8"
            noValidate
          >
            <div>
              <label
                htmlFor="reset-recovery-email"
                className="block font-sans text-xs font-medium uppercase tracking-widest text-stone-500"
              >
                {de.authReset.recoveryEmailLabel}
              </label>
              <input
                id="reset-recovery-email"
                type="email"
                required
                autoComplete="email"
                value={recoveryEmail}
                onChange={(e) => {
                  setRecoveryEmail(e.target.value);
                }}
                placeholder={de.login.emailPlaceholder}
                className="mt-3 w-full rounded-sm border border-stone-200 bg-white px-4 py-3 font-serif text-base text-stone-900 placeholder-stone-300 focus:border-stone-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
              />
            </div>
            <div className="mt-6">
              <label
                htmlFor="reset-recovery-otp"
                className="block font-sans text-xs font-medium uppercase tracking-widest text-stone-500"
              >
                {de.authReset.recoveryOtpLabel}
              </label>
              <input
                id="reset-recovery-otp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                value={recoveryOtp}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setRecoveryOtp(digits);
                }}
                placeholder={de.login.otpFallbackPlaceholder}
                className="mt-3 w-full max-w-[12rem] rounded-sm border border-stone-200 bg-white px-4 py-3 text-center font-mono text-lg tracking-[0.4em] text-stone-900 placeholder-stone-300 focus:border-stone-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
              />
            </div>
            {recoveryError && (
              <p
                role="alert"
                className="mt-4 font-sans text-xs font-medium uppercase tracking-widest text-rose-600"
              >
                {recoveryError}
              </p>
            )}
            <Button
              type="submit"
              disabled={
                recoverySubmitting || recoveryEmail.trim().length === 0 || recoveryOtp.length !== 6
              }
              className="mt-6"
            >
              {recoverySubmitting
                ? de.authReset.recoverySubmitting
                : `${de.authReset.recoverySubmit} →`}
            </Button>
          </form>
        </>
      )}

      {phase.kind === 'form' && (
        <>
          <p className="mt-4 font-serif text-base text-stone-700">{de.authReset.formExplainer}</p>
          <form
            onSubmit={(e) => void handleFormSubmit(e)}
            className="mt-8 rounded-sm border border-stone-200 bg-white p-6 md:p-8"
            noValidate
          >
            <div>
              <label
                htmlFor="reset-password"
                className="block font-sans text-xs font-medium uppercase tracking-widest text-stone-500"
              >
                {de.authReset.passwordLabel}
              </label>
              <input
                id="reset-password"
                type="password"
                required
                autoComplete="new-password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                }}
                className="mt-3 w-full rounded-sm border border-stone-200 bg-white px-4 py-3 font-serif text-base text-stone-900 focus:border-stone-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
              />
            </div>
            <div className="mt-6">
              <label
                htmlFor="reset-password-repeat"
                className="block font-sans text-xs font-medium uppercase tracking-widest text-stone-500"
              >
                {de.authReset.passwordRepeatLabel}
              </label>
              <input
                id="reset-password-repeat"
                type="password"
                required
                autoComplete="new-password"
                value={passwordRepeat}
                onChange={(e) => {
                  setPasswordRepeat(e.target.value);
                }}
                className="mt-3 w-full rounded-sm border border-stone-200 bg-white px-4 py-3 font-serif text-base text-stone-900 focus:border-stone-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
              />
            </div>
            {formError && (
              <p
                role="alert"
                className="mt-4 font-sans text-xs font-medium uppercase tracking-widest text-rose-600"
              >
                {formError}
              </p>
            )}
            <Button
              type="submit"
              disabled={submitting || password.length === 0 || passwordRepeat.length === 0}
              className="mt-6"
            >
              {submitting ? de.authReset.submitting : `${de.authReset.submit} →`}
            </Button>
          </form>
        </>
      )}
    </main>
  );
}
